import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";
import SpeakButton from "../../common/SpeakButton";
import styles from "../css/AISentenceCard.module.css";
import Sentence from "../model/Sentence";
import { getVoiceFile, makeBase64String } from "../../voice/util/voice";
import AudioPlayer from "react-h5-audio-player";
import { getLanguageString } from "../manager/sentenceSettings";
import ChipButton from "../../common/view/ChipButton";

import { ReactComponent as MoreImage } from "../../../img/iconImgAllMore.svg";
import IconButton from "../../common/view/IconButton";

interface AISentenceCardProps {
  sentence?: Sentence;
  isMoreSentenceLoading?: boolean;
  onClickLanguageButton: (e: React.MouseEvent<HTMLDivElement>) => void;
  onClickMoreSentenceButton: (e: React.MouseEvent<HTMLDivElement>) => void;
  onClickMoreButton: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/** sentence.word와 일치하는 문장 내 단어를 노란색 배경으로 강조한다. */
function renderSentenceText(sentence: Sentence) {
  const word = sentence.word?.trim() ?? "";
  if (word.length === 0) {
    return sentence.sentence;
  }

  const text = sentence.sentence;
  const lowerText = text.toLowerCase();
  const lowerWord = word.toLowerCase();

  const nodes: React.ReactNode[] = [];
  let location = 0;

  while (location < text.length) {
    const found = lowerText.indexOf(lowerWord, location);
    if (found === -1) break;

    if (found > location) {
      nodes.push(text.slice(location, found));
    }
    nodes.push(
      <mark key={found} className={styles.highlightedWord}>
        {text.slice(found, found + word.length)}
      </mark>
    );
    location = found + word.length;
  }

  if (nodes.length === 0) {
    return text;
  }

  if (location < text.length) {
    nodes.push(text.slice(location));
  }

  return nodes;
}

export default function AISentenceCard(props: AISentenceCardProps) {
  const { t } = useTranslation();

  const [isPlayingSpeech, setIsPlayingSpeech] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string>();

  useEffect(() => {
    setAudioSrc(undefined);
    setIsPlayingSpeech(false);
  }, [props.sentence]);

  function handleOnClickSpeechButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    speak();
  }

  function handleOnClickMoreSentenceButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
    if (props.isMoreSentenceLoading === true) {
      return;
    }
    props.onClickMoreSentenceButton(e);
  }

  async function speak() {
    if (props.sentence === undefined) {
      return;
    }

    const text = props.sentence.sentence;

    setIsPlayingSpeech(true);

    try {
      const audio = await getVoiceFile(text, "");
      setAudioSrc(makeBase64String(audio));
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  if (props.sentence === undefined) {
    return null;
  }

  const isLoading = props.isMoreSentenceLoading === true;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.topContainer}>
          <img
            alt=""
            className={styles.aiGenerateChipImage}
            src="copmAiGeneratedChip.svg"
          />
          <IconButton
            showing={true}
            icon={MoreImage}
            color="var(--icon-secondary-color)"
            size="24px"
            onClick={props.onClickMoreButton}
          />
        </div>

        <div className={styles.textOuterContainer}>
          <div
            className={styles.textContainer}
            style={{ opacity: isLoading ? 0.1 : 1 }}
          >
            <span className={styles.sentenceText}>
              {renderSentenceText(props.sentence)}
            </span>
            <span className={styles.definitionText}>
              {props.sentence.transition}
            </span>
          </div>

          {isLoading && (
            <div className={styles.loadingContainer}>
              <ClipLoader
                loading={true}
                size={24}
                color="var(--icon-active-color)"
              />
            </div>
          )}
        </div>

        <div className={styles.bottomContainer}>
          <div className={styles.bottomLeftContainer}>
            <ChipButton
              open={true}
              text={getLanguageString()}
              variant="secondary"
              onClickAction={props.onClickLanguageButton}
            />

            <ChipButton
              open={true}
              text={t("MoreExamples")}
              variant={isLoading ? "secondaryDisable" : "secondary"}
              onClickAction={handleOnClickMoreSentenceButton}
            />
          </div>

          <div className={styles.bottomRightContainer}>
            <SpeakButton
              fontSize="small"
              isPlaying={isPlayingSpeech}
              onClick={handleOnClickSpeechButton}
            />
          </div>
        </div>

        {audioSrc && (
          <AudioPlayer
            autoPlay={true}
            src={audioSrc}
            style={{ display: "none" }}
            onEnded={() => {
              setIsPlayingSpeech(false);
              setAudioSrc(undefined);
            }}
          />
        )}
      </div>
    </>
  );
}
